/**
 * Client-side search for the Froonze favourites / wishlist modal.
 * Filters against the full wishlist index, not only the current paginated page.
 */
(function () {
  const wishlistArrayKeys = [
    'wishlist',
    'wishlistItems',
    'wishList',
    'wish_list',
    'list',
    'items',
    'entries',
    'products',
    'lines',
    'lineItems'
  ];

  const wishlistInitEvents = [
    'frcp.wishlist.initialize',
    'frcp.wishlist.update',
    'frcp.wishlist.installed'
  ];

  let wishlistIndex = new Map();
  let wishlistRoot = null;
  let activeQuery = '';
  let searchModeActive = false;
  let enrichPromise = null;
  let froonzeListFetched = false;
  let froonzeVariantCount = 0;
  const froonzeVariantIdSet = new Set();
  const productJsonCache = new Map();
  const productJsonPromises = new Map();
  const productByIdCache = new Map();
  const nativeCardTemplates = new Map();
  let defaultNativeCardTemplate = null;

  function debounce(fn, wait) {
    let t;
    return function debounced(...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function getFavouritesSearchStrings() {
    const s = window.theme && window.theme.strings && window.theme.strings.favourites;
    return {
      placeholder: (s && s.searchPlaceholder) || 'Search by name or SKU…',
      label: (s && s.searchLabel) || 'Search favourites',
      reset: (s && s.searchReset) || 'Clear search',
      noResults: (s && s.noResults) || 'No favourites match your search.',
      resultCount: (s && s.resultCount) || '{{ count }} matches',
      resultCountTotal: (s && s.resultCountTotal) || '{{ count }} of {{ total }} favourites'
    };
  }

  function formatFavouritesSearchResultCount(count, total) {
    if (count === total) {
      return getFavouritesSearchStrings().resultCount.replace('{{ count }}', String(count));
    }
    return getFavouritesSearchStrings().resultCountTotal
      .replace('{{ count }}', String(count))
      .replace('{{ total }}', String(total));
  }

  function normalizeShopifyId(v) {
    if (v == null) return null;
    const s = String(v).trim();
    const gid = s.match(/\/(\d+)$/);
    if (gid) return gid[1];
    return /^\d+$/.test(s) ? s : null;
  }

  function firstNonEmptyValue(...vals) {
    for (let i = 0; i < vals.length; i++) {
      const v = vals[i];
      if (v != null && String(v).trim() !== '') return v;
    }
    return null;
  }

  function buildWishlistIndexKey(entry) {
    const vid = normalizeShopifyId(entry && entry.variantId);
    if (vid) return `v:${vid}`;
    const pid = normalizeShopifyId(entry && entry.productId);
    if (pid) return `p:${pid}`;
    if (entry && entry.handle) return `h:${entry.handle}`;
    return null;
  }

  function variantImageUrl(product, variant) {
    const sources = [
      variant && variant.featured_image,
      variant && variant.image,
      product && product.featured_image,
      product && product.image,
      product && product.images && product.images[0]
    ];
    for (let i = 0; i < sources.length; i++) {
      const img = sources[i];
      if (!img) continue;
      if (typeof img === 'string') return img;
      if (img.src) return img.src;
      if (img.url) return img.url;
    }
    return '';
  }

  function productPageUrl(handle, variantId) {
    if (!handle) return '';
    const vid = normalizeShopifyId(variantId);
    return vid ? `/products/${handle}?variant=${vid}` : `/products/${handle}`;
  }

  function mergeWishlistIndexEntries(existing, incoming) {
    if (!incoming) return existing;
    const base = existing ? { ...existing } : {};
    Object.keys(incoming).forEach((k) => {
      if (incoming[k] != null && incoming[k] !== '') {
        base[k] = incoming[k];
      }
    });
    return base;
  }

  function buildIndexEntryFromWishlistItem(item) {
    if (!item || typeof item !== 'object') return null;

    const product = item.product && typeof item.product === 'object' ? item.product : {};
    const variant = item.variant && typeof item.variant === 'object' ? item.variant : {};

    const productId = normalizeShopifyId(firstNonEmptyValue(
      item.productId,
      item.product_id,
      item.shopifyProductId,
      item.shopify_product_id,
      product.id,
      product.productId
    ));

    const variantId = normalizeShopifyId(firstNonEmptyValue(
      item.variantId,
      item.variant_id,
      item.variantID,
      item.shopifyVariantId,
      item.shopify_variant_id,
      variant.id,
      variant.variantId
    ));

    if (!productId && !variantId) return null;

    const handle = firstNonEmptyValue(item.handle, product.handle, item.productHandle);
    const title = firstNonEmptyValue(item.title, product.title, item.name, product.name, item.productTitle);
    const vendor = firstNonEmptyValue(item.vendor, product.vendor);
    const sku = firstNonEmptyValue(item.sku, variant.sku, item.variantSku);
    const variantTitle = firstNonEmptyValue(variant.title, item.variantTitle, item.variant_title);
    const image = firstNonEmptyValue(
      item.image,
      item.imageUrl,
      item.image_url,
      item.featured_image,
      variant.featured_image,
      variant.image,
      product.featured_image,
      product.image
    );
    const imageUrl = typeof image === 'string' ? image : (image && (image.src || image.url)) || '';

    return {
      productId: productId || null,
      variantId: variantId || null,
      handle: handle ? String(handle) : '',
      title: title ? String(title) : '',
      variantTitle: variantTitle ? String(variantTitle) : '',
      vendor: vendor ? String(vendor) : '',
      sku: sku ? String(sku) : '',
      image: imageUrl,
      url: productPageUrl(handle, variantId)
    };
  }

  function getSelectedVariantIdFromCard(card, product) {
    if (card) {
      const selectVariant = card.querySelector('select')?.selectedOptions?.[0]?._value?.id;
      if (selectVariant) return normalizeShopifyId(selectVariant);
      const datasetVariant = normalizeShopifyId(card.dataset.variantId);
      if (datasetVariant) return datasetVariant;
    }
    if (product) {
      const fromProduct = normalizeShopifyId(firstNonEmptyValue(
        product.selectedVariantId,
        product.variantId,
        product.selected_variant_id
      ));
      if (fromProduct) return fromProduct;
      if (Array.isArray(product.variants) && product.variants.length === 1) {
        return normalizeShopifyId(product.variants[0].id);
      }
    }
    return null;
  }

  function buildIndexEntryFromProductJson(product, variantId) {
    const vid = normalizeShopifyId(variantId);
    if (!product || !vid) return null;

    const variant = (product.variants || []).find((v) => normalizeShopifyId(v.id) === vid);
    const rawVariantTitle = variant && variant.title ? String(variant.title) : '';
    const variantTitle = /^default\s*title$/i.test(rawVariantTitle) ? '' : rawVariantTitle;

    return {
      productId: normalizeShopifyId(product.id),
      variantId: vid,
      handle: product.handle ? String(product.handle) : '',
      title: product.title ? String(product.title) : '',
      variantTitle,
      vendor: product.vendor ? String(product.vendor) : '',
      sku: variant && variant.sku ? String(variant.sku) : '',
      image: variantImageUrl(product, variant),
      url: productPageUrl(product.handle, vid)
    };
  }

  function buildIndexEntryFromProductDetail(product, el) {
    if (!product || typeof product !== 'object') {
      return buildIndexEntryFromDomCard(el);
    }

    const productId = normalizeShopifyId(firstNonEmptyValue(
      product.id,
      product.productId,
      el && el.dataset && el.dataset.id
    ));

    const variantId = getSelectedVariantIdFromCard(el, product);
    const variant = variantId && Array.isArray(product.variants)
      ? product.variants.find((v) => normalizeShopifyId(v.id) === variantId)
      : null;
    const rawVariantTitle = variant && variant.title ? String(variant.title) : '';
    const variantTitle = /^default\s*title$/i.test(rawVariantTitle) ? '' : rawVariantTitle;

    const entry = {
      productId,
      variantId,
      handle: product.handle ? String(product.handle) : '',
      title: product.title ? String(product.title) : '',
      variantTitle,
      vendor: product.vendor ? String(product.vendor) : '',
      sku: variant && variant.sku ? String(variant.sku) : '',
      image: variantImageUrl(product, variant),
      url: productPageUrl(product.handle, variantId)
    };

    return mergeWishlistIndexEntries(buildIndexEntryFromDomCard(el), entry);
  }

  function buildIndexEntryFromDomCard(card) {
    if (!card || !(card instanceof Element)) return null;

    const productId = normalizeShopifyId(card.dataset.id);
    const variantId = getSelectedVariantIdFromCard(card, null);
    const titleEl = card.querySelector(
      '.frcp-product__title, .frcp-product__name, .frcp-product__heading, h3, h4, [class*="product-title"]'
    );
    const link = card.querySelector('a[href*="/products/"]');
    let handle = card.dataset.handle || '';

    if (!handle && link) {
      const match = String(link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
      if (match) handle = match[1];
    }

    if (!productId && !handle && !variantId) return null;

    const img = card.querySelector('img');
    const image = img ? (img.currentSrc || img.src || '') : '';
    const skuEl = card.querySelector(
      '.frcp-product__sku, [class*="product-sku"], [class*="variant-sku"], [data-sku]'
    );
    let sku = '';
    if (skuEl) {
      sku = (skuEl.dataset.sku || skuEl.textContent || '').replace(/\s+/g, ' ').trim();
    }

    return {
      productId: productId || null,
      variantId: variantId || null,
      handle,
      title: titleEl ? titleEl.textContent.replace(/\s+/g, ' ').trim() : '',
      vendor: '',
      sku,
      image,
      url: productPageUrl(handle, variantId)
    };
  }

  function extractWishlistArraysFromPayload(detail) {
    const arrays = [];
    if (!detail) return arrays;

    if (Array.isArray(detail)) {
      arrays.push(detail);
      return arrays;
    }

    if (typeof detail !== 'object') return arrays;

    const roots = [detail];
    if (detail.data !== undefined) roots.push(detail.data);

    roots.forEach((root) => {
      if (!root || typeof root !== 'object') return;
      if (Array.isArray(root)) {
        arrays.push(root);
        return;
      }
      wishlistArrayKeys.forEach((key) => {
        const arr = root[key];
        if (Array.isArray(arr) && arr.length) arrays.push(arr);
      });
    });

    return arrays;
  }

  function upsertWishlistIndexEntry(entry) {
    if (!entry) return;
    const key = buildWishlistIndexKey(entry);
    if (!key) return;
    wishlistIndex.set(key, mergeWishlistIndexEntries(wishlistIndex.get(key), entry));
    const vid = normalizeShopifyId(entry.variantId);
    if (vid) froonzeVariantIdSet.add(vid);
  }

  function extractFroonzeListsPayloadRoot(detail) {
    if (!detail || typeof detail !== 'object') return null;
    if (detail.lists || detail.product_id_lists) return detail;
    if (detail.data && typeof detail.data === 'object'
      && (detail.data.lists || detail.data.product_id_lists)) {
      return detail.data;
    }
    return null;
  }

  /**
   * Froonze wishlist shape:
   * { lists: { default: { handle: [variantId, ...] } },
   *   product_id_lists: { default: { productId: [variantId, ...] } } }
   */
  function ingestFroonzeHandleAndVariantLists(detail) {
    const root = extractFroonzeListsPayloadRoot(detail);
    if (!root) return false;

    const activeList = root.activeList || 'default';
    const handleMap = root.lists && root.lists[activeList];
    const productIdMap = root.product_id_lists && root.product_id_lists[activeList];
    const variantEntries = new Map();

    if (handleMap && typeof handleMap === 'object') {
      Object.keys(handleMap).forEach((handle) => {
        const variantIds = handleMap[handle];
        if (!Array.isArray(variantIds)) return;
        variantIds.forEach((vid) => {
          const variantId = normalizeShopifyId(vid);
          if (!variantId) return;
          variantEntries.set(variantId, mergeWishlistIndexEntries(variantEntries.get(variantId), {
            variantId,
            handle: String(handle),
            url: productPageUrl(handle, variantId)
          }));
        });
      });
    }

    if (productIdMap && typeof productIdMap === 'object') {
      Object.keys(productIdMap).forEach((productIdKey) => {
        const productId = normalizeShopifyId(productIdKey);
        const variantIds = productIdMap[productIdKey];
        if (!productId || !Array.isArray(variantIds)) return;
        variantIds.forEach((vid) => {
          const variantId = normalizeShopifyId(vid);
          if (!variantId) return;
          variantEntries.set(variantId, mergeWishlistIndexEntries(variantEntries.get(variantId), {
            variantId,
            productId
          }));
        });
      });
    }

    if (!variantEntries.size) return false;

    variantEntries.forEach((entry) => upsertWishlistIndexEntry(entry));
    froonzeVariantCount = variantEntries.size;
    return true;
  }

  function ingestWishlistArraysFromPayload(detail) {
    const arrays = extractWishlistArraysFromPayload(detail);
    arrays.forEach((arr) => {
      arr.forEach((item) => {
        if (typeof item === 'string' || typeof item === 'number') {
          const variantId = normalizeShopifyId(item);
          if (!variantId) return;
          upsertWishlistIndexEntry({ variantId, productId: null });
          return;
        }
        const entry = buildIndexEntryFromWishlistItem(item);
        if (entry) upsertWishlistIndexEntry(entry);
      });
    });
  }

  function ingestWishlistPayloadRecursively(detail, depth) {
    if (detail == null || depth > 6) return;
    if (Array.isArray(detail)) {
      detail.forEach((item) => {
        if (typeof item === 'string' || typeof item === 'number') {
          const variantId = normalizeShopifyId(item);
          if (variantId) upsertWishlistIndexEntry({ variantId, productId: null });
          return;
        }
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          const entry = buildIndexEntryFromWishlistItem(item);
          if (entry) upsertWishlistIndexEntry(entry);
          else ingestWishlistPayloadRecursively(item, depth + 1);
        }
      });
      return;
    }
    if (typeof detail === 'object') {
      Object.keys(detail).forEach((key) => {
        ingestWishlistPayloadRecursively(detail[key], depth + 1);
      });
    }
  }

  function ingestWishlistFromFrcpGlobal() {
    if (typeof frcp === 'undefined' || !frcp.wishlist) return;
    const wl = frcp.wishlist;
    if (ingestFroonzeHandleAndVariantLists(wl)) return;
    wishlistArrayKeys.forEach((key) => {
      if (Array.isArray(wl[key])) ingestWishlistArraysFromPayload(wl[key]);
    });
    if (wl.data) ingestWishlistArraysFromPayload(wl.data);
    ingestWishlistPayloadRecursively(wl, 0);
  }

  function findIndexEntryInFrcpWishlist(variantId) {
    const vid = normalizeShopifyId(variantId);
    if (!vid || typeof frcp === 'undefined' || !frcp.wishlist) return null;

    const listsRoot = extractFroonzeListsPayloadRoot(frcp.wishlist);
    if (listsRoot) {
      let found = null;
      const activeList = listsRoot.activeList || 'default';
      const handleMap = listsRoot.lists && listsRoot.lists[activeList];
      const productIdMap = listsRoot.product_id_lists && listsRoot.product_id_lists[activeList];

      if (handleMap) {
        Object.keys(handleMap).forEach((handle) => {
          if ((handleMap[handle] || []).some((id) => normalizeShopifyId(id) === vid)) {
            found = mergeWishlistIndexEntries(found, {
              variantId: vid,
              handle: String(handle),
              url: productPageUrl(handle, vid)
            });
          }
        });
      }
      if (productIdMap) {
        Object.keys(productIdMap).forEach((productIdKey) => {
          if ((productIdMap[productIdKey] || []).some((id) => normalizeShopifyId(id) === vid)) {
            found = mergeWishlistIndexEntries(found, {
              variantId: vid,
              productId: normalizeShopifyId(productIdKey)
            });
          }
        });
      }
      if (found) return found;
    }

    let found = null;
    function walk(node, depth) {
      if (!node || depth > 6) return;
      if (Array.isArray(node)) {
        node.forEach((item) => walk(item, depth + 1));
        return;
      }
      if (typeof node !== 'object') return;

      const entry = buildIndexEntryFromWishlistItem(node);
      if (entry && normalizeShopifyId(entry.variantId) === vid) {
        found = mergeWishlistIndexEntries(found, entry);
        return;
      }
      Object.keys(node).forEach((key) => walk(node[key], depth + 1));
    }

    walk(frcp.wishlist, 0);
    return found;
  }

  async function fetchAndIngestFroonzeWishlistApi(force) {
    if (froonzeListFetched && !force) return;
    froonzeListFetched = true;

    const endpoints = ['/apps/wishlist/list', '/apps/wishlist'];
    for (let i = 0; i < endpoints.length; i++) {
      try {
        const res = await fetch(endpoints[i], {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });
        if (!res.ok) continue;
        const json = await res.json();
        if (!ingestFroonzeHandleAndVariantLists(json)) {
          ingestWishlistArraysFromPayload(json);
          ingestWishlistPayloadRecursively(json, 0);
        }
        break;
      } catch (err) { /* ignore */ }
    }
  }

  function getFavouritedVariantIds() {
    if (froonzeVariantIdSet.size) {
      return Array.from(froonzeVariantIdSet).map(normalizeShopifyId).filter(Boolean);
    }
    const ids = new Set();
    wishlistIndex.forEach((entry, key) => {
      const vid = normalizeShopifyId(entry.variantId) || (key.startsWith('v:') ? key.slice(2) : null);
      if (vid) ids.add(vid);
    });
    return Array.from(ids);
  }

  function getAllWishlistIndexEntries() {
    const variantIds = getFavouritedVariantIds();
    if (variantIds.length) {
      return variantIds.map((vid) => mergeWishlistIndexEntries(
        { variantId: vid },
        wishlistIndex.get(`v:${vid}`) || {}
      ));
    }

    const byVariant = new Map();
    wishlistIndex.forEach((entry) => {
      const vid = normalizeShopifyId(entry.variantId);
      if (vid) {
        byVariant.set(vid, mergeWishlistIndexEntries(byVariant.get(vid), entry));
        return;
      }
      if (!entry.productId && !entry.title && !entry.handle) return;
      const key = buildWishlistIndexKey(entry);
      if (key) byVariant.set(key, mergeWishlistIndexEntries(byVariant.get(key), entry));
    });
    return Array.from(byVariant.values());
  }

  function getTotalFavouritesCount() {
    const variantTotal = getFavouritedVariantIds().length;
    const entryTotal = getAllWishlistIndexEntries().length;
    return Math.max(variantTotal, froonzeVariantCount, entryTotal);
  }

  function findCachedProductForVariantId(variantId) {
    const vid = normalizeShopifyId(variantId);
    if (!vid) return null;

    for (const product of productByIdCache.values()) {
      if ((product.variants || []).some((v) => normalizeShopifyId(v.id) === vid)) {
        return product;
      }
    }
    for (const product of productJsonCache.values()) {
      if ((product.variants || []).some((v) => normalizeShopifyId(v.id) === vid)) {
        return product;
      }
    }
    return null;
  }

  async function enrichIndexEntriesWithProductData() {
    const variantIds = getFavouritedVariantIds();
    await Promise.all(variantIds.map(async (vid) => {
      let entry = mergeWishlistIndexEntries({ variantId: vid }, wishlistIndex.get(`v:${vid}`) || {});

      const missingMetadata = !entry.handle || !entry.title || !entry.image || !entry.sku;

      if (missingMetadata) {
        const fromFrcp = findIndexEntryInFrcpWishlist(vid);
        if (fromFrcp) entry = mergeWishlistIndexEntries(entry, fromFrcp);
      }

      if (!entry.handle || !entry.title || !entry.image || !entry.sku) {
        const cached = findCachedProductForVariantId(vid);
        if (cached) {
          entry = mergeWishlistIndexEntries(entry, buildIndexEntryFromProductJson(cached, vid));
        }
      }

      if (entry.handle && (!entry.title || !entry.image || !entry.sku)) {
        const product = await fetchAndCacheProductJson(entry.handle);
        if (product) {
          entry = mergeWishlistIndexEntries(entry, buildIndexEntryFromProductJson(product, vid));
        }
      }

      upsertWishlistIndexEntry(entry);
    }));
  }

  async function fetchAndCacheProductJson(handle) {
    if (!handle) return null;
    if (productJsonCache.has(handle)) {
      return productJsonCache.get(handle);
    }
    if (productJsonPromises.has(handle)) {
      return productJsonPromises.get(handle);
    }

    const promise = (async () => {
      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) return null;
        const json = await res.json();
        productJsonCache.set(handle, json);
        const pid = normalizeShopifyId(json.id);
        if (pid) productByIdCache.set(String(pid), json);
        return json;
      } catch (err) {
        return null;
      } finally {
        productJsonPromises.delete(handle);
      }
    })();

    productJsonPromises.set(handle, promise);
    return promise;
  }

  function getVisibleWishlistCardsSignature() {
    if (!wishlistRoot) return '';
    return queryAllWithinWishlistRoot(getWishlistSearchRoot(wishlistRoot), '.frcp-product:not(.kbx-favourites-search-card)')
      .map((card) => card.dataset.id || card.textContent.slice(0, 40))
      .join('|');
  }

  function clearWishlistCardCustomizationState(card) {
    if (!card) return;
    card.querySelectorAll('[data-kbx-spark-mounted], [data-kbx-spark-scheduled], [data-kbx-wishlist-bound]')
      .forEach((el) => {
        delete el.dataset.kbxSparkMounted;
        delete el.dataset.kbxSparkScheduled;
        delete el.dataset.kbxWishlistBound;
      });
    const actions = card.querySelector('.frcp-product__actions');
    if (actions) {
      actions.innerHTML = '';
      delete actions.dataset.kbxSparkMounted;
      delete actions.dataset.kbxSparkScheduled;
    }
    card.querySelector('.frcp-product__price-container')?.remove();
    const deleteBtn = card.querySelector('.frcp-product__delete');
    if (deleteBtn) delete deleteBtn.dataset.kbxWishlistBound;
  }

  function cacheNativeWishlistCardTemplate(card) {
    if (!card || !(card instanceof HTMLElement)) return;
    if (card.classList.contains('kbx-favourites-search-card')) return;
    if (!card.classList.contains('frcp-product')) return;

    const productId = normalizeShopifyId(card.dataset.id);
    const prepared = card.cloneNode(true);
    clearWishlistCardCustomizationState(prepared);

    if (productId && !nativeCardTemplates.has(productId)) {
      nativeCardTemplates.set(productId, prepared);
    }
    if (!defaultNativeCardTemplate) {
      defaultNativeCardTemplate = prepared.cloneNode(true);
    }
  }

  function getNativeWishlistCardTemplate(productId) {
    const pid = normalizeShopifyId(productId);
    if (pid && nativeCardTemplates.has(pid)) {
      return nativeCardTemplates.get(pid);
    }
    return defaultNativeCardTemplate;
  }

  function updateCardVariantSelect(card, variantId) {
    const select = card.querySelector('select');
    if (!select || !variantId) return;

    const vid = normalizeShopifyId(variantId);
    let matched = false;

    for (let i = 0; i < select.options.length; i++) {
      const opt = select.options[i];
      const optId = normalizeShopifyId(
        (opt._value && opt._value.id) || opt.value || (opt.dataset && opt.dataset.variantId)
      );
      if (optId === vid) {
        select.selectedIndex = i;
        matched = true;
        break;
      }
    }

    if (!matched) {
      const wrapper = select.closest(
        '.frcp-product__variant, .frcp-product__variants, [class*="variant-select"], [class*="product-variant"]'
      );
      if (wrapper) wrapper.remove();
      else select.remove();
    }
  }

  function populateSearchCardFromEntry(card, entry) {
    const displayTitle = getWishlistEntryDisplayTitle(entry);

    if (entry.productId) card.dataset.id = entry.productId;
    else delete card.dataset.id;
    if (entry.variantId) card.dataset.variantId = entry.variantId;
    else delete card.dataset.variantId;
    if (entry.handle) card.dataset.handle = entry.handle;
    else delete card.dataset.handle;

    const titleEl = card.querySelector(
      '.frcp-product__title, .frcp-product__name, .frcp-product__heading, [class*="product-title"]'
    );
    if (titleEl) {
      const link = titleEl.tagName === 'A' ? titleEl : titleEl.querySelector('a');
      if (link) {
        link.textContent = displayTitle;
        if (entry.url) link.href = entry.url;
      } else {
        titleEl.textContent = displayTitle;
      }
    }

    card.querySelectorAll('a[href*="/products/"]').forEach((anchor) => {
      if (entry.url) anchor.href = entry.url;
    });

    const img = card.querySelector('.frcp-product__image img, .frcp-product__media img, a img, img');
    if (img && entry.image) {
      img.src = entry.image;
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      if (entry.title) img.alt = entry.title;
    }

    updateCardVariantSelect(card, entry.variantId);
    clearWishlistCardCustomizationState(card);
  }

  function createFallbackWishlistCard() {
    const card = document.createElement('div');
    card.className = 'frcp-product';
    card.innerHTML = `
      <a class="frcp-product__image" href="#">
        <img src="" alt="" loading="lazy">
      </a>
      <div class="frcp-product__content">
        <a class="frcp-product__title" href="#"></a>
        <div class="frcp-product__actions"></div>
        <button type="button" class="frcp-product__delete" aria-label="Remove from favourites">&times;</button>
      </div>
    `;
    return card;
  }

  function ingestWishlistIndexFromVisibleCards() {
    if (!wishlistRoot) return;
    queryAllWithinWishlistRoot(getWishlistSearchRoot(wishlistRoot), '.frcp-product:not(.kbx-favourites-search-card)')
      .forEach((card) => {
        cacheNativeWishlistCardTemplate(card);
        const entry = buildIndexEntryFromDomCard(card);
        if (entry) upsertWishlistIndexEntry(entry);
      });
  }

  function removeSearchCards(root) {
    queryAllWithinWishlistRoot(getWishlistSearchRoot(root), '.kbx-favourites-search-card')
      .forEach((card) => card.remove());
  }

  function findPaginationControls(pagination) {
    if (!pagination) return { pageButtons: [], nextButton: null };

    const pageButtons = [];
    let nextButton = null;

    pagination.querySelectorAll('button, a, [role="button"]').forEach((el) => {
      const text = ((el.getAttribute('aria-label') || '') + ' ' + el.textContent).trim();
      if (/^\d+$/.test(text)) {
        pageButtons.push(el);
        return;
      }
      if (/next|forward|›|»/i.test(text)) {
        if (!el.disabled && el.getAttribute('aria-disabled') !== 'true') {
          nextButton = el;
        }
      }
    });

    return { pageButtons, nextButton };
  }

  async function ingestWishlistCardsFromAllPages() {
    if (!wishlistRoot) return;

    ingestWishlistIndexFromVisibleCards();

    const pagination = findPaginationEl(wishlistRoot);
    if (!pagination) return;

    const { pageButtons, nextButton } = findPaginationControls(pagination);

    if (pageButtons.length > 1) {
      const firstPage = pageButtons[0];
      for (let i = 0; i < pageButtons.length; i++) {
        pageButtons[i].click();
        await new Promise((resolve) => setTimeout(resolve, 450));
        ingestWishlistIndexFromVisibleCards();
      }
      if (firstPage) {
        firstPage.click();
        await new Promise((resolve) => setTimeout(resolve, 300));
      }
      ingestWishlistIndexFromVisibleCards();
      return;
    }

    let safety = 12;
    let previousSignature = getVisibleWishlistCardsSignature();

    while (safety-- > 0) {
      const paginationNow = findPaginationEl(wishlistRoot);
      const { nextButton: nextBtn } = findPaginationControls(paginationNow);
      if (!nextBtn) break;

      nextBtn.click();
      await new Promise((resolve) => setTimeout(resolve, 450));
      ingestWishlistIndexFromVisibleCards();

      const currentSignature = getVisibleWishlistCardsSignature();
      if (currentSignature === previousSignature) break;
      previousSignature = currentSignature;
    }
  }

  async function buildWishlistIndexForSearch() {
    if (enrichPromise) return enrichPromise;

    enrichPromise = (async () => {
      ingestWishlistFromFrcpGlobal();
      await fetchAndIngestFroonzeWishlistApi();
      await ingestWishlistCardsFromAllPages();
      ingestWishlistFromFrcpGlobal();
      await enrichIndexEntriesWithProductData();
    })().finally(() => {
      enrichPromise = null;
    });

    return enrichPromise;
  }

  function normalizeSearchText(value) {
    return String(value || '').toLowerCase().trim();
  }

  function normalizeSkuForSearch(value) {
    return normalizeSearchText(value).replace(/[\s\-._/]/g, '');
  }

  function doesIndexEntryMatchSearchQuery(entry, query) {
    if (!query) return true;

    const haystack = [
      entry.title,
      entry.variantTitle,
      entry.vendor,
      entry.handle,
      entry.sku,
      entry.productId,
      entry.variantId
    ].map(normalizeSearchText).join(' ');

    if (haystack.includes(query)) return true;

    const entrySku = entry.sku ? String(entry.sku) : '';
    if (entrySku) {
      const normalizedEntrySku = normalizeSkuForSearch(entrySku);
      const normalizedQuerySku = normalizeSkuForSearch(query);
      if (normalizedQuerySku && normalizedEntrySku.includes(normalizedQuerySku)) return true;
    }

    const tokens = query.split(/\s+/).filter(Boolean);
    if (!tokens.length) return true;
    return tokens.every((token) => {
      if (haystack.includes(token)) return true;
      const tokenSku = normalizeSkuForSearch(token);
      const entrySkuNormalized = normalizeSkuForSearch(entrySku);
      return tokenSku && entrySkuNormalized.includes(tokenSku);
    });
  }

  function getElementFromEventDetail(detail) {
    if (!detail) return null;
    if (detail instanceof HTMLElement) return detail;
    return detail.el || detail.element || detail.container || detail.node || null;
  }

  function getWishlistSearchRoot(root) {
    if (root instanceof ShadowRoot) {
      return root.querySelector('[class*="wishlist"]')
        || root.querySelector('[class*="frcp"]')
        || root.firstElementChild
        || root;
    }
    if (root instanceof HTMLElement && root.shadowRoot) {
      const inner = root.shadowRoot.querySelector('[class*="wishlist"]')
        || root.shadowRoot.querySelector('[class*="frcp"]')
        || root.shadowRoot.firstElementChild;
      if (inner) return inner;
    }
    return root;
  }

  function queryAllWithinWishlistRoot(scope, selector) {
    const results = [];
    if (!scope) return results;

    if (scope instanceof ShadowRoot || scope instanceof HTMLElement) {
      scope.querySelectorAll(selector).forEach((el) => results.push(el));
    }
    if (scope instanceof HTMLElement && scope.shadowRoot) {
      scope.shadowRoot.querySelectorAll(selector).forEach((el) => results.push(el));
    }
    return results;
  }

  function queryFirstWithinWishlistRoot(scope, selector) {
    return queryAllWithinWishlistRoot(scope, selector)[0] || null;
  }

  function elementMatchesClassPattern(el, pattern) {
    if (!el || !el.className) return false;
    return pattern.test(String(el.className));
  }

  function findWishlistModalRoot(anchorEl) {
    if (!anchorEl || !(anchorEl instanceof Element)) return null;

    let node = anchorEl;
    let best = null;

    while (node && node !== document.documentElement) {
      if (node instanceof HTMLElement) {
        if (elementMatchesClassPattern(node, /wishlist|frcp-wishlist|frcp-modal|frcp-panel|frcp-drawer|frcp-content|frcp-main/i)) {
          best = node;
        }
        if (node.querySelector('.frcp-pagination') || node.querySelector('[class*="pagination"]')) {
          return node;
        }
      }
      node = node.parentElement;
    }

    if (best) return best;

    const rootNode = anchorEl.getRootNode();
    if (rootNode instanceof ShadowRoot) {
      return anchorEl.closest('[class*="wishlist"]')
        || anchorEl.closest('[class*="frcp"]')
        || anchorEl.parentElement
        || rootNode.host;
    }

    return anchorEl.parentElement || anchorEl;
  }

  function injectFavouritesSearchStylesIntoShadow(shadowRoot) {
    if (!(shadowRoot instanceof ShadowRoot)) return;
    if (shadowRoot.querySelector('[data-kbx-favourites-search-style]')) return;

    const themeHref = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
      .map((link) => link.href)
      .find((href) => href.includes('favourites-search'));

    if (themeHref) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = themeHref;
      link.dataset.kbxFavouritesSearchStyle = 'true';
      shadowRoot.insertBefore(link, shadowRoot.firstChild);
    }
  }

  function findNativeWishlistProductList(root) {
    const scope = getWishlistSearchRoot(root);
    if (!(scope instanceof HTMLElement) && !(scope instanceof ShadowRoot)) return null;

    const firstProduct = queryFirstWithinWishlistRoot(scope, '.frcp-product:not(.kbx-favourites-search-card)');
    if (firstProduct && firstProduct.parentElement) {
      return firstProduct.parentElement;
    }
    return queryFirstWithinWishlistRoot(scope, '[class*="wishlist"][class*="list"]')
      || queryFirstWithinWishlistRoot(scope, '[class*="product-list"]')
      || queryFirstWithinWishlistRoot(scope, '.frcp-wishlist__products');
  }

  function findPaginationEl(root) {
    const scope = getWishlistSearchRoot(root);
    if (!scope) return null;
    return queryFirstWithinWishlistRoot(scope, '.frcp-pagination')
      || queryFirstWithinWishlistRoot(scope, '[class*="pagination"]')
      || queryFirstWithinWishlistRoot(scope, 'nav[aria-label*="agination"]');
  }

  function findSearchResultsContainer(root) {
    const scope = getWishlistSearchRoot(root);
    if (!scope) return null;
    return queryFirstWithinWishlistRoot(scope, '[data-kbx-favourites-search-results]');
  }

  function removeOrphanedSearchResultContainers(root) {
    const listContainer = findNativeWishlistProductList(root);
    queryAllWithinWishlistRoot(getWishlistSearchRoot(root), '[data-kbx-favourites-search-results]').forEach((el) => {
      if (!listContainer || !listContainer.contains(el)) {
        el.remove();
      }
    });
  }

  function getOrCreateSearchResultsContainer(root, listContainer) {
    removeOrphanedSearchResultContainers(root);

    const container = listContainer || findNativeWishlistProductList(root);
    let el = container
      ? container.querySelector('[data-kbx-favourites-search-results]')
      : findSearchResultsContainer(root);

    if (el) return el;

    el = document.createElement('div');
    el.className = 'kbx-favourites-search-results';
    el.dataset.kbxFavouritesSearchResults = 'true';
    el.hidden = true;

    if (container) {
      container.appendChild(el);
    } else {
      const scope = getWishlistSearchRoot(root);
      if (scope instanceof HTMLElement || scope instanceof ShadowRoot) {
        scope.appendChild(el);
      }
    }
    return el;
  }

  function toggleNativeWishlistCardsVisibility(root, hidden) {
    queryAllWithinWishlistRoot(getWishlistSearchRoot(root), '.frcp-product:not(.kbx-favourites-search-card)')
      .forEach((card) => {
        card.classList.toggle('kbx-favourites-search-native-hidden', hidden);
      });
  }

  const sparkMountQueue = [];
  let sparkMountTimer = null;

  function processSparkCardMountQueue() {
    if (!sparkMountQueue.length) {
      sparkMountTimer = null;
      return;
    }
    const job = sparkMountQueue.shift();
    job();
    sparkMountTimer = setTimeout(processSparkCardMountQueue, 150);
  }

  function scheduleSparkCardMountForCard(card, variantId) {
    const actions = card.querySelector('.frcp-product__actions');
    if (!actions || actions.dataset.kbxSparkScheduled) return;
    actions.dataset.kbxSparkScheduled = 'true';

    sparkMountQueue.push(() => {
      if (!card.isConnected || actions.dataset.kbxSparkMounted) return;
      actions.dataset.kbxSparkMounted = 'true';
      if (typeof window.customizeFroonzeWishlistCard === 'function') {
        window.customizeFroonzeWishlistCard(card, { variantId });
      }
    });

    if (!sparkMountTimer) {
      sparkMountTimer = setTimeout(processSparkCardMountQueue, 0);
    }
  }

  function mountSparkCardWhenScrolledIntoView(card, variantId) {
    const scrollRoot = card.parentElement
      || card.closest('[class*="wishlist"]')
      || findNativeWishlistProductList(wishlistRoot);
    if (!('IntersectionObserver' in window) || !scrollRoot) {
      scheduleSparkCardMountForCard(card, variantId);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        scheduleSparkCardMountForCard(card, variantId);
      });
    }, { root: scrollRoot, rootMargin: '120px', threshold: 0 });

    observer.observe(card);
  }

  function getWishlistEntryDisplayTitle(entry) {
    const base = entry.title || 'Product';
    if (entry.variantTitle) return `${base} – ${entry.variantTitle}`;
    if (entry.sku) return `${base} (${entry.sku})`;
    return base;
  }

  function createSearchResultCard(entry) {
    const template = getNativeWishlistCardTemplate(entry.productId);
    const card = template ? template.cloneNode(true) : createFallbackWishlistCard();
    card.classList.add('kbx-favourites-search-card');
    populateSearchCardFromEntry(card, entry);
    mountSparkCardWhenScrolledIntoView(card, entry.variantId);
    return card;
  }

  function toggleWishlistSearchActiveState(root, active) {
    const scope = getWishlistSearchRoot(root);
    const target = scope instanceof HTMLElement ? scope : root;
    if (target && target.classList) {
      target.classList.toggle('kbx-favourites-search-active', active);
    }
  }

  function renderFavouritesSearchResults(root, matches, query) {
    sparkMountQueue.length = 0;
    if (sparkMountTimer) {
      clearTimeout(sparkMountTimer);
      sparkMountTimer = null;
    }

    const listContainer = findNativeWishlistProductList(root);
    const resultsEl = getOrCreateSearchResultsContainer(root, listContainer);
    const paginationEl = findPaginationEl(root);
    const statusEl = queryFirstWithinWishlistRoot(getWishlistSearchRoot(root), '.kbx-favourites-search__status');

    removeSearchCards(root);
    toggleWishlistSearchActiveState(root, true);
    toggleNativeWishlistCardsVisibility(root, true);

    if (listContainer) {
      listContainer.hidden = false;
      listContainer.classList.add('kbx-favourites-search-list--active');
    }
    if (paginationEl) paginationEl.hidden = true;

    resultsEl.hidden = true;
    resultsEl.innerHTML = '';

    if (!matches.length) {
      resultsEl.hidden = false;
      const empty = document.createElement('p');
      empty.className = 'kbx-favourites-search-results__empty';
      empty.textContent = getFavouritesSearchStrings().noResults;
      resultsEl.appendChild(empty);
    } else if (listContainer) {
      const fragment = document.createDocumentFragment();
      matches.forEach((entry) => {
        fragment.appendChild(createSearchResultCard(entry));
      });
      listContainer.appendChild(fragment);
    }

    if (statusEl && matches.length) {
      statusEl.textContent = formatFavouritesSearchResultCount(matches.length, getTotalFavouritesCount());
    } else if (statusEl) {
      statusEl.textContent = '';
    }
  }

  function restoreNativeWishlistView(root) {
    searchModeActive = false;
    const listContainer = findNativeWishlistProductList(root);
    const resultsEl = listContainer
      ? listContainer.querySelector('[data-kbx-favourites-search-results]')
      : findSearchResultsContainer(root);
    const paginationEl = findPaginationEl(root);
    const statusEl = queryFirstWithinWishlistRoot(getWishlistSearchRoot(root), '.kbx-favourites-search__status');

    removeSearchCards(root);
    toggleWishlistSearchActiveState(root, false);
    toggleNativeWishlistCardsVisibility(root, false);

    if (listContainer) {
      listContainer.hidden = false;
      listContainer.classList.remove('kbx-favourites-search-list--active');
    }
    if (paginationEl) paginationEl.hidden = false;
    if (resultsEl) {
      resultsEl.hidden = true;
      resultsEl.innerHTML = '';
    }
    if (statusEl) statusEl.textContent = '';
  }

  async function renderAllFavouritesInSearchView() {
    if (!wishlistRoot) return;
    searchModeActive = true;
    activeQuery = '';

    const statusEl = queryFirstWithinWishlistRoot(getWishlistSearchRoot(wishlistRoot), '.kbx-favourites-search__status');
    if (statusEl) statusEl.textContent = 'Loading…';

    await buildWishlistIndexForSearch();
    renderFavouritesSearchResults(wishlistRoot, getAllWishlistIndexEntries(), '');
  }

  const debouncedHandleFavouritesSearchInput = debounce(async (query) => {
    if (!wishlistRoot) return;

    const normalized = normalizeSearchText(query);
    activeQuery = normalized;

    if (!normalized) {
      if (searchModeActive) {
        await renderAllFavouritesInSearchView();
      } else {
        restoreNativeWishlistView(wishlistRoot);
      }
      return;
    }

    searchModeActive = true;
    const statusEl = queryFirstWithinWishlistRoot(getWishlistSearchRoot(wishlistRoot), '.kbx-favourites-search__status');
    if (statusEl) statusEl.textContent = 'Searching…';

    await buildWishlistIndexForSearch();

    const matches = getAllWishlistIndexEntries().filter((entry) => doesIndexEntryMatchSearchQuery(entry, normalized));
    renderFavouritesSearchResults(wishlistRoot, matches, normalized);
  }, 300);

  function handleFavouritesSearchInput(query) {
    debouncedHandleFavouritesSearchInput(query);
  }

  function injectFavouritesSearchBar(root) {
    const mountTarget = getWishlistSearchRoot(root);
    if (!mountTarget) return;

    const mountEl = mountTarget instanceof HTMLElement
      ? mountTarget
      : (mountTarget instanceof ShadowRoot ? mountTarget.firstElementChild : null);
    if (!mountEl || !(mountEl instanceof HTMLElement)) return;
    if (mountEl.dataset.kbxFavouritesSearchInit) {
      wishlistRoot = mountEl;
      return;
    }

    const rootNode = mountEl.getRootNode();
    if (rootNode instanceof ShadowRoot) {
      injectFavouritesSearchStylesIntoShadow(rootNode);
    }

    mountEl.dataset.kbxFavouritesSearchInit = 'true';

    const wrap = document.createElement('div');
    wrap.className = 'kbx-favourites-search search';
    wrap.dataset.kbxFavouritesSearchWrap = 'true';

    const label = document.createElement('label');
    label.className = 'kbx-favourites-search__label visually-hidden';
    label.textContent = getFavouritesSearchStrings().label;

    const form = document.createElement('div');
    form.className = 'kbx-favourites-search__form search__form block relative';

    const input = document.createElement('input');
    input.type = 'search';
    input.className = 'search__input w-full input kbx-favourites-search__input';
    input.placeholder = getFavouritesSearchStrings().placeholder;
    input.autocomplete = 'off';
    input.setAttribute('aria-label', getFavouritesSearchStrings().label);
    const inputId = `kbx-favourites-search-input-${Date.now()}`;
    input.id = inputId;
    label.setAttribute('for', inputId);

    const resetBtn = document.createElement('button');
    resetBtn.type = 'button';
    resetBtn.className = 'kbx-favourites-search__reset btn btn--icon visually-hidden';
    resetBtn.setAttribute('aria-label', getFavouritesSearchStrings().reset);
    resetBtn.innerHTML = '&times;';

    const status = document.createElement('p');
    status.className = 'kbx-favourites-search__status text-sm';
    status.setAttribute('aria-live', 'polite');

    input.addEventListener('focus', () => {
      if (!searchModeActive) renderAllFavouritesInSearchView();
    });

    input.addEventListener('input', () => {
      const hasValue = input.value.trim().length > 0;
      resetBtn.classList.toggle('visually-hidden', !hasValue);
      handleFavouritesSearchInput(input.value);
    });

    resetBtn.addEventListener('click', () => {
      input.value = '';
      resetBtn.classList.add('visually-hidden');
      searchModeActive = false;
      restoreNativeWishlistView(wishlistRoot);
      input.blur();
    });

    form.appendChild(input);
    form.appendChild(resetBtn);
    wrap.appendChild(label);
    wrap.appendChild(form);
    wrap.appendChild(status);

    const listContainer = findNativeWishlistProductList(mountEl);
    if (listContainer && listContainer.parentElement) {
      listContainer.parentElement.insertBefore(wrap, listContainer);
    } else {
      mountEl.insertBefore(wrap, mountEl.firstChild);
    }

    wishlistRoot = mountEl;
  }

  function mountFavouritesSearchBarIfNeeded(anchorEl) {
    const mount = findWishlistModalRoot(anchorEl);
    if (!mount) return;
    injectFavouritesSearchBar(mount);
  }

  function handleFroonzeWishlistDataUpdate(detail) {
    if (!ingestFroonzeHandleAndVariantLists(detail)) {
      ingestWishlistArraysFromPayload(detail);
      ingestWishlistPayloadRecursively(detail, 0);
    }
    ingestWishlistFromFrcpGlobal();
  }

  wishlistInitEvents.forEach((evt) => {
    document.addEventListener(evt, (e) => {
      if (evt === 'frcp.wishlist.update') froonzeListFetched = false;
      handleFroonzeWishlistDataUpdate(e.detail);
      if (searchModeActive && wishlistRoot) {
        const query = activeQuery;
        buildWishlistIndexForSearch().then(() => {
          const matches = query
            ? getAllWishlistIndexEntries().filter((entry) => doesIndexEntryMatchSearchQuery(entry, query))
            : getAllWishlistIndexEntries();
          renderFavouritesSearchResults(wishlistRoot, matches, query);
        });
      } else if (activeQuery && wishlistRoot) {
        handleFavouritesSearchInput(activeQuery);
      }
    });
  });

  document.addEventListener('kbx.froonze.wishlistData', (e) => {
    handleFroonzeWishlistDataUpdate(e.detail);
  });

  document.addEventListener('kbx.froonze.wishlistVariantIds', (e) => {
    const payload = e.detail || {};
    const ids = Array.isArray(payload) ? payload : payload.ids;
    if (payload.count) {
      froonzeVariantCount = payload.count;
    } else if (Array.isArray(ids)) {
      froonzeVariantCount = ids.length;
    }
    if (!Array.isArray(ids)) return;
    ids.forEach((id) => {
      const variantId = normalizeShopifyId(id);
      if (variantId) {
        froonzeVariantIdSet.add(variantId);
        upsertWishlistIndexEntry({ variantId, productId: null });
      }
    });
    if (searchModeActive && wishlistRoot) {
      buildWishlistIndexForSearch().then(() => {
        const query = activeQuery;
        const matches = query
          ? getAllWishlistIndexEntries().filter((entry) => doesIndexEntryMatchSearchQuery(entry, query))
          : getAllWishlistIndexEntries();
        renderFavouritesSearchResults(wishlistRoot, matches, query);
      });
    }
  });

  document.addEventListener('frcp.wishlist.productMounted', (e) => {
    const card = e.detail && e.detail.el;
    if (!card) return;

    cacheNativeWishlistCardTemplate(card);

    if (!wishlistRoot || !wishlistRoot.isConnected) {
      mountFavouritesSearchBarIfNeeded(card);
    }

    const entry = buildIndexEntryFromProductDetail(e.detail.product, card);
    if (entry) upsertWishlistIndexEntry(entry);
  });

  document.addEventListener('frcp.wishlist.mainMounted', (e) => {
    const el = getElementFromEventDetail(e.detail);
    if (!el) return;
    mountFavouritesSearchBarIfNeeded(el);
    ingestWishlistFromFrcpGlobal();
  });

  document.addEventListener('click', (evt) => {
    if (!wishlistRoot) return;
    const paginationControl = evt.target && evt.target.closest(
      '.frcp-pagination button, .frcp-pagination a, [class*="pagination"] button, [class*="pagination"] a'
    );
    if (!paginationControl || !wishlistRoot.contains(paginationControl)) return;
    setTimeout(ingestWishlistIndexFromVisibleCards, 500);
  }, true);
})();
